// Ported from Octo.Mobile@79a7c69 src/utils/formatters.ts
import { formatDateDisplay, formatDateTimeDisplay, formatTimeDisplay } from './dateValue';

/** Tarih (dd.MM.yyyy) — "YYYY-MM-DD" yerel gün olarak okunur, dilim kayması olmaz */
export function formatDate(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  return formatDateDisplay(value);
}

/** Tarih + saat (dd.MM.yyyy HH:mm) */
export function formatDateTime(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  return formatDateTimeDisplay(value);
}

/** Saat (HH:mm) */
export function formatTime(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  return formatTimeDisplay(value);
}

/**
 * Format a number with optional scale (decimal places).
 */
export function formatNumber(value: unknown, scale?: number | null): string {
  if (value === null || value === undefined) return '';
  const num = typeof value === 'number' ? value : Number(value);
  if (isNaN(num)) return String(value);

  if (scale !== null && scale !== undefined) {
    return num.toLocaleString('tr-TR', {
      minimumFractionDigits: scale,
      maximumFractionDigits: scale,
    });
  }
  return num.toLocaleString('tr-TR');
}

/**
 * Format a boolean value.
 */
export function formatBoolean(value: unknown): string {
  if (value === true || value === 1 || value === '1' || value === 'true') return 'Evet';
  if (value === false || value === 0 || value === '0' || value === 'false') return 'Hayır';
  return '';
}

/**
 * Format a cell value based on editor type and/or DB type.
 */
export function formatCellValue(
  value: unknown,
  editorTypeName: string | null,
  _displayFormat: string | null,
  scale?: number | null,
  fieldDbTypeName?: string | null,
): string {
  if (value === null || value === undefined) return '';

  // 1. Format by editorTypeName (highest priority)
  switch (editorTypeName) {
    case 'DateEdit':
      return formatDate(value);
    case 'DateTimeEdit':
      return formatDateTime(value);
    case 'TimeEdit':
      return formatTime(value);
    case 'CheckEdit':
      return formatBoolean(value);
    case 'SpinEdit':
      return formatNumber(value, scale);
  }

  // 2. Fallback: format by fieldDbTypeName
  if (fieldDbTypeName) {
    const dbType = fieldDbTypeName.toLowerCase();
    if (dbType === 'datetime' || dbType === 'datetime2' || dbType === 'smalldatetime') {
      return formatDateTime(value);
    }
    if (dbType === 'date') {
      return formatDate(value);
    }
    if (dbType === 'time') {
      return formatTime(value);
    }
    if (dbType === 'bit') {
      return formatBoolean(value);
    }
    if (dbType === 'int' || dbType === 'bigint' || dbType === 'smallint' || dbType === 'tinyint') {
      return formatNumber(value, 0);
    }
    if (
      dbType === 'decimal' ||
      dbType === 'numeric' ||
      dbType === 'money' ||
      dbType === 'float' ||
      dbType === 'real'
    ) {
      return formatNumber(value, scale);
    }
  }

  return String(value);
}

/**
 * Resolve the card title for a given row — mirrors MasterRecordCard title logic.
 * Priority: label columns joined with ' | ' → displayFieldName → first visible column → fallback.
 */
export function resolveCardTitle(
  row: Record<string, unknown>,
  columns: Array<{
    name: string;
    visible: boolean;
    displayOrder: number;
    dataType: string;
    label?: boolean;
    editorTypeName: string | null;
    displayFormat: string | null;
    scale: number | null;
    fieldDbTypeName: string | null;
  }>,
  displayFieldName: string | null | undefined,
  fallback: string,
): string {
  const visibleCols = columns
    .filter((c) => c.visible && c.dataType !== 'guid' && c.dataType !== 'binary')
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const labelCols = visibleCols.filter((c) => c.label);

  if (labelCols.length > 0) {
    const val = labelCols
      .map((col) =>
        formatCellValue(row[col.name], col.editorTypeName, col.displayFormat, col.scale, col.fieldDbTypeName),
      )
      .filter((v) => v != null && v !== '')
      .join(' | ');
    return val || fallback;
  }

  const titleCol = displayFieldName
    ? visibleCols.find((c) => c.name.toLowerCase() === displayFieldName.toLowerCase()) || visibleCols[0]
    : visibleCols[0];

  if (titleCol) {
    const val = formatCellValue(
      row[titleCol.name],
      titleCol.editorTypeName,
      titleCol.displayFormat,
      titleCol.scale,
      titleCol.fieldDbTypeName,
    );
    return val || fallback;
  }

  return fallback;
}
